'use client';

import { useEffect, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { Loader2, Send, CheckCircle2 } from 'lucide-react';

import { contactSchema, type ContactInput } from '@/lib/contact-validation';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';

const MESSAGE_MAX = 5000;

export function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const [renderedAt, setRenderedAt] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    setRenderedAt(Date.now());
  }, []);

  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: '', email: '', subject: '', message: '', consent: false },
  });

  const messageValue = useWatch({ control, name: 'message' });
  const messageLength = messageValue?.length ?? 0;

  const onSubmit = async (values: ContactInput) => {
    setServerError(null);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values,
          website: honeypot,
          _t: renderedAt,
        }),
      });

      let payload: { ok?: boolean; error?: string; fieldErrors?: Record<string, string> } = {};
      try {
        payload = await res.json();
      } catch {
        payload = {};
      }

      if (!res.ok || !payload.ok) {
        if (payload.fieldErrors) {
          for (const [field, message] of Object.entries(payload.fieldErrors)) {
            setError(field as keyof ContactInput, { message });
          }
        }
        const msg = payload.error ?? 'Beim Senden ist ein Fehler aufgetreten.';
        setServerError(msg);
        toast({ variant: 'destructive', title: 'Nicht gesendet', description: msg });
        return;
      }

      toast({
        title: 'Nachricht gesendet',
        description: 'Vielen Dank! Wir melden uns in der Regel innerhalb von 1–2 Werktagen.',
      });
      reset();
      setHoneypot('');
      setSubmitted(true);
    } catch {
      const msg = 'Die Verbindung konnte nicht hergestellt werden. Bitte versuchen Sie es erneut.';
      setServerError(msg);
      toast({ variant: 'destructive', title: 'Verbindungsfehler', description: msg });
    }
  };

  if (submitted) {
    return (
      <div className="border border-border bg-card p-8 sm:p-10 flex flex-col items-center text-center">
        <CheckCircle2 className="w-12 h-12 text-[var(--signal)] mb-5" />
        <h3 className="font-display text-xl font-semibold mb-2">Nachricht ist angekommen</h3>
        <p className="text-[#8794A0] text-[0.95rem] mb-6 max-w-md">
          Vielen Dank für Ihre Anfrage. Wir melden uns in der Regel innerhalb von 1–2 Werktagen
          per E-Mail bei Ihnen.
        </p>
        <button
          type="button"
          onClick={() => {
            setSubmitted(false);
            setServerError(null);
          }}
          className="font-mono text-[0.78rem] text-[var(--paper)] border-b border-border pb-0.5 hover:text-[var(--signal)] hover:border-[var(--signal)] transition-colors"
        >
          Weitere Nachricht senden
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="border border-border bg-card p-6 sm:p-8 space-y-5"
      autoComplete="on"
    >
      {/* Honeypot (fuer echte Nutzer unsichtbar) */}
      <div aria-hidden="true" className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden">
        <label htmlFor="website-field">Nicht ausfüllen</label>
        <textarea
          id="website-field"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-[0.82rem] text-[#A6B2BD]">
            Name <span className="text-[var(--signal)]">*</span>
          </Label>
          <Input
            id="name"
            autoComplete="name"
            aria-invalid={!!errors.name}
            className="bg-background"
            {...register('name')}
          />
          {errors.name && <p className="text-[0.78rem] text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-[0.82rem] text-[#A6B2BD]">
            E-Mail <span className="text-[var(--signal)]">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            className="bg-background"
            {...register('email')}
          />
          {errors.email && <p className="text-[0.78rem] text-destructive">{errors.email.message}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="subject" className="text-[0.82rem] text-[#A6B2BD]">
          Betreff <span className="text-[var(--signal)]">*</span>
        </Label>
        <Input
          id="subject"
          autoComplete="off"
          aria-invalid={!!errors.subject}
          className="bg-background"
          {...register('subject')}
        />
        {errors.subject && <p className="text-[0.78rem] text-destructive">{errors.subject.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-[0.82rem] text-[#A6B2BD]">
          Nachricht <span className="text-[var(--signal)]">*</span>
        </Label>
        <Textarea
          id="message"
          rows={6}
          aria-invalid={!!errors.message}
          className="bg-background resize-y"
          {...register('message')}
        />
        <div className="flex items-center justify-between">
          {errors.message ? (
            <p className="text-[0.78rem] text-destructive">{errors.message.message}</p>
          ) : (
            <span />
          )}
          <span className="text-[0.72rem] font-mono text-[#8794A0]">
            {messageLength}/{MESSAGE_MAX}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <Controller
          control={control}
          name="consent"
          render={({ field }) => (
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                className="mt-0.5"
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
              />
              <Label htmlFor="consent" className="text-[0.82rem] leading-relaxed text-[#A6B2BD] font-normal">
                Ich habe die{' '}
                <Link href="/datenschutz" className="underline hover:text-[var(--signal)]">
                  Datenschutzerklärung
                </Link>{' '}
                gelesen und bin mit der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage
                einverstanden. <span className="text-[var(--signal)]">*</span>
              </Label>
            </div>
          )}
        />
        {errors.consent && <p className="text-[0.78rem] text-destructive">{errors.consent.message}</p>}
      </div>

      {serverError && (
        <div className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-[0.84rem] text-destructive">
          {serverError}
        </div>
      )}

      <div className="flex items-center justify-between gap-4 pt-1">
        <p className="text-[0.72rem] font-mono text-[#8794A0]">
          Pflichtfelder mit <span className="text-[var(--signal)]">*</span> markiert
        </p>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Wird gesendet …
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Nachricht senden
            </>
          )}
        </button>
      </div>
    </form>
  );
}
